import { Checkbox, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { OrganizationDeliveryPoint } from '../../../organizations/types/organization.type';
import DeliveryPointTypeChip from '../../../delivery-points/components/Chips/DeliveryPointTypeChip.component';

const useStyles = makeStyles((theme) => ({
  card: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.25rem',
    padding: '0.5rem 0.75rem 0.75rem 0.25rem',
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: 6,
    cursor: 'pointer',
    transition: 'border-color 120ms, background-color 120ms',

    '&:hover': {
      borderColor: theme.palette.grey[500],
    },
  },
  selected: {
    borderColor: theme.palette.admin.primary.main,
    backgroundColor: `${theme.palette.admin.primary.main}0F`,

    '&:hover': {
      borderColor: theme.palette.admin.primary.dark,
    },
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
    paddingTop: '0.5rem',
    minWidth: 0,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    flexWrap: 'wrap',
  },
  name: {
    fontWeight: 600,
  },
  secondary: {
    fontSize: 13,
    opacity: 0.75,
  },
  missing: {
    fontSize: 13,
    fontStyle: 'italic',
    opacity: 0.55,
  },
}));

type Props = {
  deliveryPoint: OrganizationDeliveryPoint;
  isSelected: boolean;
  onToggle: (isSelected: boolean) => void;
};

const formatAddress = ({ address }: OrganizationDeliveryPoint): string =>
  [address?.street, [address?.zipCode, address?.city].filter(Boolean).join(' ')].filter(Boolean).join(', ');

export default function DeliveryPointSelectionCard({ deliveryPoint, isSelected, onToggle }: Props) {
  const classes = useStyles();
  const address = formatAddress(deliveryPoint);

  return (
    <div
      className={`${classes.card} ${isSelected ? classes.selected : ''}`}
      role="checkbox"
      aria-checked={isSelected}
      tabIndex={0}
      onClick={() => onToggle(!isSelected)}
      onKeyDown={(event) => {
        if (event.key === ' ' || event.key === 'Enter') {
          event.preventDefault();
          onToggle(!isSelected);
        }
      }}
    >
      {/* La case suit l'état de la carte : le clic est capté par la carte entière. */}
      <Checkbox checked={isSelected} tabIndex={-1} color="default" inputProps={{ 'aria-hidden': true }} />
      <div className={classes.body}>
        <div className={classes.header}>
          <Typography className={classes.name}>{deliveryPoint.name}</Typography>
          <DeliveryPointTypeChip type={deliveryPoint.type} />
        </div>
        {address ? (
          <Typography className={classes.secondary}>{address}</Typography>
        ) : (
          <Typography className={classes.missing}>Adresse non renseignée</Typography>
        )}
        {deliveryPoint.invoicingNumber ? (
          <Typography className={classes.secondary}>N° {deliveryPoint.invoicingNumber}</Typography>
        ) : (
          <Typography className={classes.missing}>Aucun numéro de facturation</Typography>
        )}
      </div>
    </div>
  );
}
